import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Loader2, CreditCard, CheckCircle2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

interface Subscription {
  id: string;
  plan_id: string;
  status: string;
  valuation_credits_remaining: number | null;
  closing_pack_credits_remaining: number | null;
  auditor_pdf_credits_remaining: number | null;
  period_start?: string;
  period_end?: string;
}

interface BillingPlan {
  id: string;
  name: string;
  monthly_usd: number;
  monthly_zar: number;
  monthly_gbp: number;
  monthly_aed: number;
  valuation_credits: number | null;
  closing_pack_credits: number | null;
  auditor_pdf_credits: number | null;
  features: string[];
}

export default function BillingSettings({ companyId }: { companyId: string }) {
  const [plans, setPlans] = useState<BillingPlan[]>([]);
  const [sub, setSub] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [currency, setCurrency] = useState('USD');
  const [upgrading, setUpgrading] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    try {
      const [plansResult, subResult, currencyResult] = await Promise.all([
        supabase.from('billing_plans').select('*').order('monthly_usd'),
        supabase.from('subscriptions').select('*').eq('company_id', companyId).maybeSingle(),
        supabase
          .from('company_financials')
          .select('currency')
          .eq('company_id', companyId)
          .maybeSingle(),
      ]);

      setPlans((plansResult.data || []) as BillingPlan[]);
      setSub(subResult.data as Subscription | null);
      setCurrency(currencyResult.data?.currency || 'USD');
    } catch (error: any) {
      toast.error('Failed to load billing info');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }

  async function upgrade(planId: string) {
    setUpgrading(planId);
    try {
      const { data, error } = await supabase.functions.invoke('billing-apply-plan', {
        body: { companyId, planId },
      });

      if (error) throw error;

      if (data?.checkout_url && data.checkout_url !== '#') {
        window.open(data.checkout_url, '_blank');
        toast.success('Redirecting to payment...');
      } else {
        toast.success('Plan applied successfully');
        load();
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to apply plan');
    } finally {
      setUpgrading(null);
    }
  }

  useEffect(() => {
    if (companyId) {
      load();

      // Subscribe to subscription changes
      const channel = supabase
        .channel('billing-changes')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'subscriptions',
            filter: `company_id=eq.${companyId}`,
          },
          () => load()
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [companyId]);

  const formatPrice = (plan: BillingPlan) => {
    const amount =
      currency === 'ZAR'
        ? plan.monthly_zar
        : currency === 'GBP'
          ? plan.monthly_gbp
          : currency === 'AED'
            ? plan.monthly_aed
            : plan.monthly_usd;
    return `${currency} ${amount.toFixed(2)}`;
  };

  const formatCredits = (credits: number | null) => {
    if (credits === null || credits === -1) return 'Unlimited';
    return credits.toString();
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return (
          <Badge className="bg-green-500">
            <CheckCircle2 className="h-3 w-3 mr-1" />
            Active
          </Badge>
        );
      case 'PENDING':
        return <Badge variant="secondary">Pending</Badge>;
      case 'EXPIRED':
        return (
          <Badge variant="destructive">
            <AlertCircle className="h-3 w-3 mr-1" />
            Expired
          </Badge>
        );
      case 'CANCELLED':
        return <Badge variant="outline">Cancelled</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getCreditProgress = (
    current: number | null,
    plan: BillingPlan,
    type: 'valuation' | 'closing_pack' | 'auditor_pdf'
  ) => {
    const total =
      type === 'valuation'
        ? plan.valuation_credits
        : type === 'closing_pack'
          ? plan.closing_pack_credits
          : plan.auditor_pdf_credits;

    if (current === null || current === -1 || total === null || total === -1) return 100;
    if (total === 0) return 0;
    return (current / total) * 100;
  };

  if (loading) {
    return (
      <Card className="border-border bg-card">
        <CardContent className="p-8 flex justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  const currentPlan = plans.find((p) => p.id === sub?.plan_id);

  return (
    <Card className="border-border bg-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CreditCard className="h-5 w-5" />
          Billing & Subscription
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Current Subscription */}
        {sub && (
          <div className="rounded-lg border p-4 space-y-4">
            <div className="flex justify-between items-center">
              <div>
                <div className="text-sm text-muted-foreground">Current Plan</div>
                <div className="text-xl font-semibold">{currentPlan?.name || sub.plan_id}</div>
              </div>
              {getStatusBadge(sub.status)}
            </div>

            {/* Credit Balances */}
            <div className="space-y-3">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-muted-foreground">Valuation Credits</span>
                  <span className="font-medium">
                    {formatCredits(sub.valuation_credits_remaining)}
                  </span>
                </div>
                {currentPlan &&
                  sub.valuation_credits_remaining !== null &&
                  sub.valuation_credits_remaining !== -1 && (
                    <Progress
                      value={getCreditProgress(
                        sub.valuation_credits_remaining,
                        currentPlan,
                        'valuation'
                      )}
                    />
                  )}
              </div>

              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-muted-foreground">Closing Pack Credits</span>
                  <span className="font-medium">
                    {formatCredits(sub.closing_pack_credits_remaining)}
                  </span>
                </div>
                {currentPlan &&
                  sub.closing_pack_credits_remaining !== null &&
                  sub.closing_pack_credits_remaining !== -1 && (
                    <Progress
                      value={getCreditProgress(
                        sub.closing_pack_credits_remaining,
                        currentPlan,
                        'closing_pack'
                      )}
                    />
                  )}
              </div>

              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-muted-foreground">Auditor PDF Credits</span>
                  <span className="font-medium">
                    {formatCredits(sub.auditor_pdf_credits_remaining)}
                  </span>
                </div>
                {currentPlan &&
                  sub.auditor_pdf_credits_remaining !== null &&
                  sub.auditor_pdf_credits_remaining !== -1 && (
                    <Progress
                      value={getCreditProgress(
                        sub.auditor_pdf_credits_remaining,
                        currentPlan,
                        'auditor_pdf'
                      )}
                    />
                  )}
              </div>
            </div>
          </div>
        )}

        {/* Available Plans */}
        <div>
          <h3 className="text-lg font-semibold mb-3">Available Plans</h3>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Plan</TableHead>
                <TableHead>Price/Month</TableHead>
                <TableHead className="text-center">Valuation</TableHead>
                <TableHead className="text-center">Closing Pack</TableHead>
                <TableHead className="text-center">Auditor PDF</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {plans.map((plan) => (
                <TableRow key={plan.id} className={sub?.plan_id === plan.id ? 'bg-muted/50' : ''}>
                  <TableCell>
                    <div className="font-medium">{plan.name}</div>
                    {sub?.plan_id === plan.id && (
                      <Badge variant="outline" className="mt-1">
                        Current
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>{formatPrice(plan)}</TableCell>
                  <TableCell className="text-center">
                    {formatCredits(plan.valuation_credits)}
                  </TableCell>
                  <TableCell className="text-center">
                    {formatCredits(plan.closing_pack_credits)}
                  </TableCell>
                  <TableCell className="text-center">
                    {formatCredits(plan.auditor_pdf_credits)}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      onClick={() => upgrade(plan.id)}
                      disabled={upgrading !== null || sub?.plan_id === plan.id}
                      variant={sub?.plan_id === plan.id ? 'outline' : 'default'}
                    >
                      {upgrading === plan.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : sub?.plan_id === plan.id ? (
                        'Active'
                      ) : (
                        'Select'
                      )}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Info Note */}
        <div className="rounded-lg bg-muted/50 p-4 text-sm text-muted-foreground">
          <p>
            Credits are consumed when you use premium features like valuation reports, closing
            packs, and auditor PDFs.
          </p>
          <p className="mt-2">Upgrade to a higher plan for more credits or unlimited access.</p>
        </div>
      </CardContent>
    </Card>
  );
}
